'use client';

import { cn } from '@ahm/common-helpers';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import React, { useRef, useEffect } from 'react';

import type { DayPlan } from '@/store/ai-planner';

interface DayTabsProps {
  days: DayPlan[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

export function DayTabs({ days, selectedDate, onSelectDate }: DayTabsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll selected tab into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
    const selectedTab = container.querySelector(
      `[data-date="${selectedDate}"]`
    ) as HTMLElement;
    if (selectedTab) {
      // Center the selected tab
      const scrollLeft =
        selectedTab.offsetLeft -
        container.offsetWidth / 2 +
        selectedTab.offsetWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [selectedDate]);

  const formatDayLabel = (dateStr: string, dayOfWeek: string) => {
    const date = new Date(dateStr);
    const dayNum = format(date, 'd', { locale: vi });
    const month = format(date, 'MM', { locale: vi });

    return {
      day: dayOfWeek,
      date: `${dayNum}/${month}`,
    };
  };

  const isToday = (dateStr: string) => {
    const today = new Date();
    const date = new Date(dateStr);
    return (
      today.getDate() === date.getDate() &&
      today.getMonth() === date.getMonth() &&
      today.getFullYear() === date.getFullYear()
    );
  };

  const isPast = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div
      ref={scrollContainerRef}
      className="flex gap-2 overflow-x-auto scrollbar-hide py-2 px-1 -mx-1"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {days.map((day) => {
        const { day: dayLabel, date } = formatDayLabel(day.date, day.dayOfWeek);
        const isSelected = day.date === selectedDate;
        const dayIsPast = isPast(day.date);
        const dayIsToday = isToday(day.date);

        return (
          <button
            key={day.date}
            data-date={day.date}
            onClick={() => onSelectDate(day.date)}
            className={cn(
              'flex items-center justify-center min-w-fit px-3 py-2 rounded-xl transition-all text-sm font-bold gap-2',
              'border',
              isSelected
                ? 'ai-planner-button-active border-primary text-white'
                : dayIsPast
                  ? 'ai-planner-button border-white/10 text-white/40'
                  : 'ai-planner-button border-white/20 text-white hover:border-primary'
            )}
          >
            <span className="whitespace-normal">
              {dayLabel} - {date}
            </span>
            {/* </span> */}
            {dayIsToday && (
              <span
                className={cn(
                  'text-[10px] mt-0.5 whitespace-nowrap',
                  isSelected ? 'text-white/80' : 'text-primary'
                )}
              >
                Hôm nay
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
